import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Container,
  ContainerContent,
  ContainerHeader,
  ContainerOverview,
  MyWorkTaskTable,
} from '@/component';
import MyWorkProjectStatistics from '@/component/MyWorkTaskList/MyWorkProjectStatistics';
import { useInfiniteProejctListQuery, useProjectCountQuery } from '@/hook';
import {
  VFlex,
  VStack,
  VTab,
  VTabContent,
  VTabTrigger,
  VTabTriggerContainer,
  cn,
  useIntersectionObserver,
} from '@/package';
import { useMainLayout } from '@/provider';
import { useAuthStore } from '@/store';

const MyWorkPage = () => {
  const { user } = useAuthStore();
  const { setTitle } = useMainLayout();
  const [workOption, setWorkOption] = useState<'works' | 'reviews'>('works');
  const [listOption, setListOption] = useState<'list' | 'statistics'>('list');

  const projectListParams = {
    workerID: workOption === 'works' ? user.id : undefined,
    reviewerID: workOption === 'reviews' ? user.id : undefined,
  };
  const {
    data: projectListData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteProejctListQuery(projectListParams);
  const { data: count } = useProjectCountQuery({ user });
  const ref = useRef<HTMLDivElement>(null);

  useIntersectionObserver(
    ref,
    () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    { threshold: 0.1 },
  );

  const infiniteProjectList =
    projectListData?.pages
      .map(page => {
        return page.data.resultData;
      })
      ?.flat() || [];

  useEffect(() => {
    setTitle('My works');
  }, []);

  const { t } = useTranslation();

  return (
    <Container className="bg-gray-100">
      <ContainerOverview>
        <VFlex className="items-center justify-center pt-6 pb-4">
          <VTab
            defaultValue="works"
            value={workOption}
            onValueChange={val => setWorkOption(val as 'works' | 'reviews')}
          >
            <VTabTriggerContainer>
              <VTabTrigger value="works">Works</VTabTrigger>
              <VTabTrigger value="reviews">Reviews</VTabTrigger>
            </VTabTriggerContainer>
          </VTab>
        </VFlex>
      </ContainerOverview>

      <VTab
        defaultValue="list"
        value={workOption === 'reviews' ? 'list' : listOption}
        onValueChange={val => setListOption(val as 'list' | 'statistics')}
        className="relative z-10 flex w-full flex-1 flex-col items-center overflow-hidden"
      >
        <ContainerHeader>
          <VFlex className="w-full py-4">
            <VTabTriggerContainer>
              <VTabTrigger value="list"> List </VTabTrigger>
              {workOption === 'works' && (
                <VTabTrigger value="statistics"> Statistics </VTabTrigger>
              )}
            </VTabTriggerContainer>
          </VFlex>
        </ContainerHeader>

        <ContainerContent>
          <VStack className="h-full w-full">
            <VStack className="h-full flex-1 pb-8">
              <VTabContent value="list">
                <VStack gap={1} className="h-full w-full overflow-x-hidden">
                  <div className={cn('pt-4')}>
                    {t('myWork.totalProjectListLength.interstreet', {
                      current: infiniteProjectList.length,
                      total: count ?? 0,
                    })}
                  </div>

                  <div className="min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto py-2">
                    <MyWorkTaskTable
                      projectList={infiniteProjectList}
                      ref={ref}
                    />
                  </div>
                </VStack>
              </VTabContent>

              <VTabContent value="statistics">
                <MyWorkProjectStatistics />
              </VTabContent>
            </VStack>
          </VStack>
        </ContainerContent>
      </VTab>
    </Container>
  );
};

export default MyWorkPage;
